/**
 * TenantContext — single source of truth for the active tenant id in the FE.
 *
 * - `tenantId` is initialised from `localStorage["tenant_id"]` so refreshes survive.
 * - `setTenantId(...)` mutates state AND persists to localStorage so the
 *   apiClient (which reads `localStorage["tenant_id"]`) picks the new value up
 *   on its next request.
 * - Components should call `useTenantId()` instead of inlining `'default'`.
 *
 * This is Step 1 of the FE Wiz-parity migration that retires hardcoded
 * `'default'` tenants across components and api service modules.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface TenantContextValue {
  tenantId: string;
  setTenantId: (id: string) => void;
}

const STORAGE_KEY = 'tenant_id';
const FALLBACK_TENANT = 'default';

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || FALLBACK_TENANT;
    } catch {
      return FALLBACK_TENANT;
    }
  });

  const setTenantId = useCallback((id: string) => {
    setTenantIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // localStorage unavailable (private mode etc.) — state still updates.
    }
  }, []);

  // Cross-tab sync: another tab logged in / switched tenant.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && e.newValue !== tenantId) {
        setTenantIdState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tenantId]);

  const value = useMemo(() => ({ tenantId, setTenantId }), [tenantId, setTenantId]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export function useTenantId(): string {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    // Outside provider — still return persisted value so legacy code paths work.
    try {
      return localStorage.getItem(STORAGE_KEY) || FALLBACK_TENANT;
    } catch {
      return FALLBACK_TENANT;
    }
  }
  return ctx.tenantId;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used inside <TenantProvider>');
  }
  return ctx;
}

// Non-hook accessor for code paths that cannot call hooks (api services).
// Reads localStorage directly so it stays in sync with TenantProvider state.
export function getCurrentTenantId(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || FALLBACK_TENANT;
  } catch {
    return FALLBACK_TENANT;
  }
}
